import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LANDING } from "@/constants/testIds";
import { Mail, Sparkles, ShieldCheck, Clock, Lock, FileEdit, Send, Eye } from "lucide-react";
import { toast } from "sonner";
import LegalFooter from "@/components/LegalFooter";

/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */

const HERO = "https://images.pexels.com/photos/10922370/pexels-photo-10922370.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=940";

export default function Landing() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const err = params.get("auth_error");
    if (err) {
      const friendly = {
        state_mismatch: "Sign-in must finish in the same browser it started in. Please try again.",
        bad_state: "That sign-in link expired. Please try again.",
        access_denied: "Google sign-in was cancelled.",
      };
      toast.error(friendly[err] || `Sign-in failed: ${err.replace(/_/g, " ")}`);
    }
    // Capture ?ref=… / ?utm_source=… so signups can be attributed to a landing page.
    const incoming = params.get("ref") || params.get("utm_source");
    if (incoming) {
      const cleaned = incoming.replace(/[^a-zA-Z0-9_\-.]/g, "").slice(0, 64);
      if (cleaned) {
        try { localStorage.setItem("ca:ref", cleaned); } catch { /* ignore */ }
        api.post("/attribution/ping", { ref: cleaned, page: "landing" }).catch(() => { /* ignore */ });
      }
    }
  }, [params]);

  useEffect(() => {
    if (!loading && user) {
      // If a Wix deep-link stashed a pending checkout, resume it.
      let pending = null;
      try { pending = localStorage.getItem("ca:auto-checkout"); } catch { /* ignore */ }
      if (pending === "monthly" || pending === "annual") {
        if (!user.onboarded) {
          // Complete onboarding first; Onboarding.jsx picks up the flag from localStorage.
          nav("/onboarding", { replace: true });
          return;
        }
        try { localStorage.removeItem("ca:auto-checkout"); } catch { /* ignore */ }
        nav(`/pricing?plan=${pending}&auto=1`, { replace: true });
        return;
      }
      nav(user.onboarded ? "/dashboard" : "/onboarding", { replace: true });
    }
  }, [user, loading, nav]);

  const startSignIn = async () => {
    setBusy(true);
    try {
      let ref;
      try { ref = localStorage.getItem("ca:ref") || undefined; } catch { ref = undefined; }
      const r = await api.get("/auth/google/start", { params: ref ? { ref, page: "landing" } : {} });
      window.location.href = r.data.authorization_url;
    } catch (e) {
      setBusy(false);
      const detail = e?.response?.data?.detail || "Could not start Google sign-in";
      toast.error(detail);
    }
  };

  return (
    <div className="min-h-screen w-full flex flex-col">
      <div className="flex-1 w-full grid lg:grid-cols-2">
      {/* Hero side */}
      <div className="relative hidden lg:block overflow-hidden">
        <img src={HERO} alt="Teacher's desk" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-black/10" />
        <div className="grain" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-md bg-white/15 backdrop-blur flex items-center justify-center border border-white/25">
              <Mail className="h-4 w-4" />
            </div>
            <span className="font-heading text-lg tracking-wide">ContactAssist</span>
          </div>
          <div className="max-w-md">
            <h2 className="font-heading text-4xl leading-tight mb-3">
              A quiet log of every parent conversation.
            </h2>
            <p className="text-sm text-white/80">
              ContactAssist reads your school inbox once a day, extracts parent messages about students, and writes them to a Google Sheet you own.
            </p>
          </div>
        </div>
      </div>

      {/* Sign-in side */}
      <div className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="mb-10" data-testid={LANDING.brand}>
            <div className="flex items-center gap-2 mb-8 lg:hidden">
              <div className="h-8 w-8 rounded-md bg-[#971a8c] text-white flex items-center justify-center">
                <Mail className="h-4 w-4" />
              </div>
              <span className="font-heading text-lg text-[#1C2321]">ContactAssist</span>
            </div>
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#8B9891] mb-3">For classroom teachers</p>
            <h1 className="font-heading text-4xl sm:text-5xl leading-[1.1] tracking-tight text-[#1C2321] font-medium mb-4">
              Sign in and let the log write itself.
            </h1>
            <p className="text-base leading-relaxed text-[#5D6D63]">
              Connect your school Gmail. Pick a time outside school hours. Every 24 hours ContactAssist appends new parent messages to a Google Sheet in your Drive.
            </p>
          </div>

          <button
            data-testid={LANDING.signInBtn}
            onClick={startSignIn}
            disabled={busy}
            className="w-full h-12 rounded-md border border-[#E8E6DF] bg-white hover:bg-[#F3F1EC] transition-colors flex items-center justify-center gap-3 text-[#1C2321] font-medium"
          >
            <GoogleIcon />
            {busy ? "Redirecting…" : "Continue with Google"}
          </button>

          <p className="mt-4 text-xs text-[#8B9891]">
            You&apos;ll grant access to your Gmail (read messages, save AI-drafted replies) and to one Google Sheet titled &ldquo;ContactAssist – Parent Contact Log&rdquo; in your Drive.{" "}
            <strong className="text-[#5D6D63]">Nothing is ever sent from your account without you clicking Send.</strong>
          </p>

          <div className="mt-6 flex items-center gap-4 text-xs text-[#8B9891]">
            <a data-testid="landing-pricing-link" href="/pricing" className="hover:text-[#971a8c] hover:underline">See pricing</a>
            <span>·</span>
            <a data-testid="landing-demo-link" href="/demo-login" className="hover:text-[#971a8c] hover:underline">Tester demo login →</a>
          </div>

          <div className="mt-10 grid gap-4">
            <Perk icon={<Clock className="h-4 w-4" />} title="Runs once a day" desc="Choose an evening time. We only wake up when you&apos;re off the clock." />
            <Perk icon={<Sparkles className="h-4 w-4" />} title="AI-triaged" desc="Claude Sonnet 4.5 reads each email, keeps only parent messages, and writes a 1–2 sentence summary." />
            <Perk icon={<ShieldCheck className="h-4 w-4" />} title="You own the log" desc="The Google Sheet lives in your Drive. Teacher Notes and Response Status are never overwritten." />
          </div>

          {/* Permissions & Privacy section */}
          <div
            data-testid="landing-permissions-section"
            className="mt-12 rounded-lg border border-[#E8E6DF] bg-[#F9F8F6] p-6"
          >
            <div className="flex items-center gap-2 mb-1">
              <Lock className="h-4 w-4 text-[#971a8c]" />
              <h3 className="font-heading text-lg text-[#1C2321] font-medium">Permissions &amp; privacy</h3>
            </div>
            <p className="text-sm text-[#5D6D63] mb-5">
              Google will show you a consent screen. Here&apos;s exactly what we ask for and what we do with it.
            </p>

            <div className="grid gap-4">
              <Permission
                icon={<Eye className="h-4 w-4" />}
                title="Read your Gmail messages"
                desc="So we can find parent emails in your Inbox and log the replies you send them from your Sent folder. We only store a short summary and metadata — never the full email body."
              />
              <Permission
                icon={<FileEdit className="h-4 w-4" />}
                title="Create draft replies in your Gmail"
                desc={
                  <>
                    When you ask for an AI-drafted reply, we save it as a{" "}
                    <strong className="text-[#1C2321]">draft</strong> in your Gmail so you can review, edit, and send it yourself. <strong className="text-[#1C2321]">We never send drafts automatically.</strong>
                  </>
                }
              />
              <Permission
                icon={<Send className="h-4 w-4" />}
                title="Send your weekly digest (opt-in only)"
                desc="If you turn on the weekly digest in Settings, we send one summary email from your own Gmail to yourself. Turn it off and we send nothing."
              />
              <Permission
                icon={<ShieldCheck className="h-4 w-4" />}
                title="Create + edit one Google Sheet in your Drive"
                desc="Just the ContactAssist parent-contact log. We can&apos;t see or touch any other file in your Drive."
              />
            </div>

            <p className="mt-5 text-xs text-[#8B9891] leading-relaxed">
              Full details in our{" "}
              <a
                href="/privacy.html"
                data-testid="landing-permissions-privacy-link"
                className="underline hover:text-[#971a8c]"
              >
                Privacy Policy
              </a>{" "}
              and{" "}
              <a
                href="/terms.html"
                data-testid="landing-permissions-terms-link"
                className="underline hover:text-[#971a8c]"
              >
                Terms of Service
              </a>
              . You can revoke ContactAssist&apos;s access at any time from your{" "}
              <a
                href="https://myaccount.google.com/permissions"
                target="_blank"
                rel="noreferrer"
                className="underline hover:text-[#971a8c]"
              >
                Google Account permissions
              </a>{" "}
              page.
            </p>
          </div>
        </div>
      </div>
      </div>

      <LegalFooter />
    </div>
  );
}

function Permission({ icon, title, desc }) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 h-8 w-8 shrink-0 rounded-md bg-white border border-[#E8E6DF] text-[#971a8c] flex items-center justify-center">
        {icon}
      </div>
      <div>
        <div className="text-sm font-medium text-[#1C2321]">{title}</div>
        <div className="text-sm text-[#5D6D63] leading-relaxed">{desc}</div>
      </div>
    </div>
  );
}

function Perk({ icon, title, desc }) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 h-8 w-8 rounded-md bg-[#F3F1EC] text-[#971a8c] flex items-center justify-center">
        {icon}
      </div>
      <div>
        <div className="text-sm font-medium text-[#1C2321]">{title}</div>
        <div className="text-sm text-[#5D6D63]">{desc}</div>
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
  );
}
